import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HeroSection from '../components/landing/HeroSection';
import ProtectionSystemSection from '../components/landing/ProtectionSystemSection';
import BNRegistrationSection from '../components/landing/BNRegistrationSection';
import StatsSection from '../components/landing/StatsSection';
import FaqSection from '../components/landing/FaqSection';
import ComparisonTable from '../components/landing/ComparisonTable';
import SignatureTypes from '../components/landing/SignatureTypes';
import TechnicalFeatures from '../components/landing/TechnicalFeatures';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const { t } = useTranslation();
  const [dynamicPlans, setDynamicPlans] = useState({
    professional: { price: 99.90, duration_months: 6 },
    premium: { price: 199.90, duration_months: 6 },
    annual: { price: 299.90, duration_months: 12 }
  });

  useEffect(() => {
    loadDynamicPrices();
    
    // Registrar listener para atualizações em tempo real
    import('../services/plansSyncService').then(({ default: plansSyncService }) => {
      const unsubscribe = plansSyncService.addListener((plans) => {
        console.log('🔄 LandingPage: Atualizando planos...');
        setDynamicPlans({
          professional: { 
            price: plans.professional?.price || 99.90,
            duration_months: plans.professional?.duration_months || 6
          },
          premium: { 
            price: plans.premium?.price || 199.90,
            duration_months: plans.premium?.duration_months || 6
          },
          annual: { 
            price: plans.annual?.price || 299.90,
            duration_months: plans.annual?.duration_months || 12
          }
        });
      });
      
      return () => unsubscribe();
    });
  }, []);

  const loadDynamicPrices = async () => {
    try {
      console.log('📥 LandingPage: Carregando planos dinâmicos...');
      const response = await axios.get(`${API_URL}/plans/config`);
      
      if (response.data.success && response.data.plans) {
        console.log('✅ LandingPage: Planos recebidos:', response.data.plans);
        
        setDynamicPlans({
          professional: { 
            price: response.data.plans.professional?.price || 99.90,
            duration_months: response.data.plans.professional?.duration_months || 6
          },
          premium: { 
            price: response.data.plans.premium?.price || 199.90,
            duration_months: response.data.plans.premium?.duration_months || 6
          },
          annual: { 
            price: response.data.plans.annual?.price || 299.90,
            duration_months: response.data.plans.annual?.duration_months || 12
          }
        });
        
        console.log('✅ LandingPage: duration_months atualizados:');
        console.log('   Professional:', response.data.plans.professional?.duration_months);
        console.log('   Premium:', response.data.plans.premium?.duration_months);
        console.log('   Annual:', response.data.plans.annual?.duration_months);
      }
    } catch (error) {
      console.error('❌ Error loading dynamic prices:', error);
    }
  };
  
  const plans = [
    {
      id: 'individual',
      name: t('plans.individual'),
      badge: t('plans.free'),
      price: 0,
      period: t('plans.alwaysFree'),
      features: [
        t('plans.features.upTo5'),
        t('plans.features.certificate'),
        t('plans.features.international'),
        t('plans.features.fileUpload'),
        t('plans.features.coauthors'),
        t('plans.features.publicVerification'),
        t('plans.features.indexing')
      ]
    },
    {
      id: 'professional',
      name: t('plans.professional'),
      badge: t('plans.mostPopular'),
      popular: true,
      price: dynamicPlans.professional.price,
      period: `${dynamicPlans.professional.duration_months} ${dynamicPlans.professional.duration_months === 1 ? 'mês' : 'meses'}`,
      features: [
        t('plans.features.upTo15'),
        t('plans.features.certificate'),
        t('plans.features.international'),
        t('plans.features.immediate'),
        t('plans.features.unlimitedUpload'),
        t('plans.features.coauthors'),
        t('plans.features.indexing'),
        t('plans.features.prioritySupport'),
        t('plans.features.reports')
      ]
    },
    {
      id: 'premium',
      name: 'Plano Premium',
      badge: 'Popular',
      price: dynamicPlans.premium.price,
      period: `${dynamicPlans.premium.duration_months} ${dynamicPlans.premium.duration_months === 1 ? 'mês' : 'meses'}`,
      subtitle: 'Obras Ilimitadas',
      features: [
        `Músicas ILIMITADAS (${dynamicPlans.premium.duration_months} ${dynamicPlans.premium.duration_months === 1 ? 'mês' : 'meses'})`,
        'Certificados digitais ilimitados',
        'Registro internacional',
        'Emissão instantânea',
        'Upload ilimitado de arquivos',
        'Registro de coautores',
        'Indexação em buscadores',
        'Proteção anti-plágio',
        'Suporte prioritário',
        'API de integração',
        'Consultoria jurídica',
        'Acesso antecipado'
      ]
    },
    {
      id: 'annual',
      name: 'Plano Anual',
      badge: '✨ MELHOR OFERTA',
      popular: true,
      highlight: true,
      price: dynamicPlans.annual.price,
      period: `${dynamicPlans.annual.duration_months} ${dynamicPlans.annual.duration_months === 1 ? 'mês' : 'meses'}`,
      subtitle: 'Compositor Profissional',
      features: [
        `✨ Músicas ILIMITADAS (${dynamicPlans.annual.duration_months} ${dynamicPlans.annual.duration_months === 1 ? 'mês' : 'meses'})`,
        '🎵 Áudios ilimitados',
        '📜 Certificados ilimitados',
        '⭐ Perfil verificado',
        '📊 Estatísticas exclusivas',
        '🎖️ Badge "Compositor Profissional"',
        '🔝 Destaque na galeria',
        '🎯 Suporte VIP prioritário',
        '🔗 URL personalizada',
        '💼 API completa',
        '📞 Consultoria ilimitada',
        '🚀 Todos os recursos premium'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <HeroSection />
      
      {/* NOVA SEÇÃO: Sistema de Proteção Dupla */}
      <ProtectionSystemSection />
      
      {/* NOVA SEÇÃO: Galeria Musical INÉDITAS */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 bg-white bg-opacity-20 backdrop-blur-sm px-6 py-3 rounded-full mb-6 border-2 border-white border-opacity-30">
              <Music className="w-6 h-6 animate-bounce" />
              <span className="font-bold text-lg">NOVO!</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {t('protectionSystem.musicalGalleryTitle')}
            </h2>
            <p className="text-xl md:text-2xl text-orange-100 mb-6 max-w-3xl mx-auto">
              {t('protectionSystem.musicalGallerySubtitle')}
            </p>
            <p className="text-lg text-orange-100 mb-8 max-w-2xl mx-auto">
              {t('protectionSystem.discoverTalents')}
            </p>
            
            <Link
              to="/gallery"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-orange-50 transition-all transform hover:scale-105 shadow-2xl"
            >
              <Music className="w-6 h-6" />
              OUVIR MÚSICAS AGORA
            </Link>
            
            <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20">
                <div className="text-3xl mb-2">🎧</div>
                <h3 className="font-bold text-lg mb-2">Ouça Completo</h3>
                <p className="text-sm text-orange-100">Áudio MP3 de alta qualidade</p>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20">
                <div className="text-3xl mb-2">📝</div>
                <h3 className="font-bold text-lg mb-2">Veja a Letra</h3>
                <p className="text-sm text-orange-100">Leia a letra completa</p>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20">
                <div className="text-3xl mb-2">✉️</div>
                <h3 className="font-bold text-lg mb-2">Fale com o Autor</h3>
                <p className="text-sm text-orange-100">Contato direto via email</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      
      {/* Nova Seção: Registro na Biblioteca Nacional */}
      <BNRegistrationSection />
      
      <StatsSection />

      {/* Registro Público - DESTAQUE */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-block bg-yellow-400 text-purple-900 px-6 py-2 rounded-full font-bold text-sm mb-6 animate-pulse">
              🌟 {t('publicRegistry.badge')}
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              {t('publicRegistry.title')}
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-4">
              {t('publicRegistry.subtitle')}
            </p>
            <p className="text-lg text-blue-200 max-w-3xl mx-auto">
              {t('publicRegistry.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="grid md:grid-cols-3 gap-8 mb-12"
          >
            {/* Feature 1 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold mb-3">{t('publicRegistry.feature1Title')}</h3>
              <p className="text-blue-100">
                {t('publicRegistry.feature1Desc')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl mb-4">🤖</div>
              <h3 className="text-2xl font-bold mb-3">{t('publicRegistry.feature2Title')}</h3>
              <p className="text-blue-100">
                {t('publicRegistry.feature2Desc')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl mb-4">🛡️</div>
              <h3 className="text-2xl font-bold mb-3">{t('publicRegistry.feature3Title')}</h3>
              <p className="text-blue-100">
                {t('publicRegistry.feature3Desc')}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 border-2 border-white/30"
          >
            <h3 className="text-3xl font-bold mb-6 text-center">{t('publicRegistry.howItWorks')}</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-green-400 text-green-900 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t('publicRegistry.step1')}</h4>
                    <p className="text-blue-100">{t('publicRegistry.step1Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-green-400 text-green-900 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t('publicRegistry.step2')}</h4>
                    <p className="text-blue-100">{t('publicRegistry.step2Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-green-400 text-green-900 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t('publicRegistry.step3')}</h4>
                    <p className="text-blue-100">{t('publicRegistry.step3Desc')}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-400 text-yellow-900 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t('publicRegistry.step4')}</h4>
                    <p className="text-blue-100">{t('publicRegistry.step4Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-400 text-yellow-900 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t('publicRegistry.step5')}</h4>
                    <p className="text-blue-100">{t('publicRegistry.step5Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-400 text-yellow-900 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">6</div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t('publicRegistry.step6')}</h4>
                    <p className="text-blue-100">{t('publicRegistry.step6Desc')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-green-500/20 border-2 border-green-400 rounded-xl">
              <p className="text-center text-lg">
                <strong className="text-green-300">{t('publicRegistry.example')}</strong> {t('publicRegistry.exampleText')}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/register"
              className="inline-block bg-yellow-400 hover:bg-yellow-300 text-purple-900 px-10 py-4 rounded-xl text-xl font-bold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
            >
              🚀 {t('publicRegistry.cta')}
            </Link>
            <p className="text-blue-200 mt-4 text-sm">
              {t('publicRegistry.ctaSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Como Funciona a Prova */}
      <section id="como-funciona" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('howItWorks.title')}</h2>
            <p className="text-xl text-gray-600">{t('howItWorks.techSubtitle')}</p>
          </motion.div>

          <SignatureTypes />

          <div className="mt-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('howItWorks.techTitle') || 'Tecnologias Utilizadas'}</h3>
            <TechnicalFeatures />
          </div>
        </div>
      </section>

      {/* Comparativo */}
      <section id="comparativo" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('comparison.title')}</h2>
            <p className="text-xl text-gray-600">{t('comparison.subtitle')}</p>
          </motion.div>

          {/* Banner Destaque - Versão Melhorada */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-8 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 rounded-2xl p-8 shadow-2xl border-2 border-yellow-400"
          >
            {/* Header */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="text-4xl">💎</span>
              <h3 className="text-2xl md:text-3xl font-black text-yellow-300 drop-shadow-lg uppercase tracking-wide">
                {t('protectionSystem.exclusiveBadgeLong')}
              </h3>
            </div>

            {/* Título Principal */}
            <div className="text-center mb-6">
              <h4 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
                <span>🎯</span>
                <span>{t('protectionSystem.twoLevelProtection')}</span>
              </h4>
              <p className="text-xl md:text-2xl font-semibold text-yellow-200 leading-relaxed">
                🏛️ Certificado Digital Imediato + Registro Oficial Opcional na Biblioteca Nacional (mediante solicitação expressa) - GRATUITO
              </p>
            </div>

            {/* Descrição */}
            <div className="text-center">
              <p className="text-lg md:text-xl text-white font-semibold">
                ✨ Dupla Camada de Segurança Jurídica até 120 dias gratuito • Proteção Completa, Reconhecimento Oficial e Zero Burocracia ✨
              </p>
            </div>

            {/* Cards de Benefícios */}
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-center">
                  <div className="text-3xl mb-2">📜</div>
                  <h5 className="font-bold text-white mb-2">Certificado Digital Imediato</h5>
                  <p className="text-sm text-blue-100">Prova criptografada com carimbo de tempo e validade legal</p>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-center">
                  <div className="text-3xl mb-2">🏛️</div>
                  <h5 className="font-bold text-white mb-2">Registro Oficial Opcional na BN</h5>
                  <p className="text-sm text-blue-100">Mediante solicitação expressa, suas obras são registradas gratuitamente na BN a cada 120 dias. Você mantém 100% dos ganhos.</p>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-center">
                  <div className="text-3xl mb-2">🔐</div>
                  <h5 className="font-bold text-white mb-2">Reconhecimento Federal</h5>
                  <p className="text-sm text-blue-100">Segurança jurídica e autoria incontestável</p>
                </div>
              </div>
            </div>
          </motion.div>

          <ComparisonTable />

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-blue-900 mb-4">{t('whyChoose.title')}</h3>
              <p className="text-blue-800 leading-relaxed mb-6">
                {t('whyChoose.description')}
              </p>
              <Link
                to="/register"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                {t('whyChoose.button')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <FaqSection />


      {/* Galeria de Letras CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left Side - Content */}
              <div className="p-12 flex flex-col justify-center">
                <div className="inline-block bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-semibold mb-4 w-fit">
                  📚 Biblioteca Pública
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Galeria Pública de Letras
                </h2>
                <p className="text-xl text-gray-600 mb-6">
                  Explore nossa biblioteca de letras registradas e protegidas. 
                  Todas as obras com certificado digital e prova de autoria.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="bg-green-100 text-green-600 rounded-full p-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <span className="font-medium">Busca por título, autor ou trecho</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="bg-green-100 text-green-600 rounded-full p-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <span className="font-medium">Filtros por gênero musical</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="bg-green-100 text-green-600 rounded-full p-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <span className="font-medium">Indexado por Google, GPT e outras IAs</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="bg-green-100 text-green-600 rounded-full p-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <span className="font-medium">Prova de anterioridade pública</span>
                  </li>
                </ul>
                <div className="flex gap-4">
                  <Link
                    to="/gallery/lyrics"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    📄 Explorar Galeria de Letras
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Right Side - Visual */}
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-12 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-10 left-10 text-8xl">🎵</div>
                  <div className="absolute bottom-20 right-10 text-7xl">📝</div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-9xl opacity-5">📚</div>
                </div>
                <div className="relative z-10 text-center text-white">
                  <div className="text-8xl mb-4">📄</div>
                  <div className="text-5xl font-bold mb-4">+1000</div>
                  <div className="text-xl font-medium">Letras Registradas</div>
                  <div className="mt-8 space-y-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                      <div className="text-sm font-medium">🔍 Busca Avançada</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                      <div className="text-sm font-medium">🎵 Todos os Gêneros</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                      <div className="text-sm font-medium">🤖 Indexação IA</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8"
          >
            <Link
              to="/tendencias"
              className="bg-gradient-to-br from-red-500 via-orange-500 to-yellow-500 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all group text-white relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 text-6xl opacity-10">🔥</div>
              <div className="text-4xl mb-3 relative z-10">🇧🇷</div>
              <h3 className="text-xl font-bold mb-2 relative z-10">
                Tendências do Brasil
              </h3>
              <p className="text-orange-50 text-sm relative z-10">
                Top 50 letras mais buscadas
              </p>
            </Link>

            <Link
              to="/gallery/lyrics"
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-purple-300 group"
            >
              <div className="text-4xl mb-3">📚</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                Explorar Letras
              </h3>
              <p className="text-gray-600 text-sm">
                Navegue por centenas de letras registradas
              </p>
            </Link>

            <Link
              to="/gallery"
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-300 group"
            >
              <div className="text-4xl mb-3">🎵</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                Galeria de Áudios
              </h3>
              <p className="text-gray-600 text-sm">
                Ouça composições inéditas protegidas
              </p>
            </Link>

            <Link
              to="/register"
              className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all group text-white"
            >
              <div className="text-4xl mb-3">✨</div>
              <h3 className="text-xl font-bold mb-2">
                Registre Suas Letras
              </h3>
              <p className="text-green-50 text-sm">
                Proteja suas composições agora mesmo
              </p>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('plans.title')}</h2>
            <p className="text-xl text-gray-600 mb-4">{t('plans.subtitle')}</p>
            
            {/* Aviso de Preços Promocionais */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg animate-pulse">
              <span className="text-2xl">🔥</span>
              <span className="font-bold text-lg">PREÇOS PROMOCIONAIS POR TEMPO LIMITADO!</span>
              <span className="text-2xl">🔥</span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white rounded-3xl p-6 border-2 ${
                  plan.highlight ? 'border-purple-500 shadow-2xl transform scale-105 ring-4 ring-purple-200' : 
                  plan.popular ? 'border-blue-500 shadow-2xl transform scale-105' : 'border-gray-200'
                } hover:shadow-xl transition-all relative overflow-hidden`}
              >
                {/* Badge Promoção no canto superior direito */}
                <div className="absolute top-4 right-4">
                  <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md transform rotate-12">
                    PROMOÇÃO
                  </div>
                </div>
                
                {plan.badge && (
                  <div className={`inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4 ${
                    plan.highlight ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                    plan.popular ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {plan.badge}
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-blue-600">R${plan.price}</span>
                  <p className="text-gray-600 mt-1">{plan.period}</p>
                  {plan.subtitle && <p className="text-sm text-gray-500 mt-1">{plan.subtitle}</p>}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className={`block w-full text-center py-3 rounded-xl font-semibold transition-all ${
                    plan.highlight
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-lg'
                      : plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.highlight ? 'COMEÇAR AGORA 🚀' : t('plans.startNow')}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Music className="w-20 h-20 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('cta.title')}</h2>
            <p className="text-xl opacity-90 mb-8">{t('cta.subtitle')}</p>
            
            <Link
              to="/register"
              className="inline-block bg-yellow-400 text-blue-900 px-10 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl transition-all transform hover:scale-105"
            >
              {t('cta.button')}
            </Link>

            <div className="grid grid-cols-3 gap-8 mt-12">
              <div>
                <p className="text-3xl font-bold">{t('cta.stat1Title')}</p>
                <p className="opacity-90">{t('cta.stat1Desc')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{t('cta.stat2Title')}</p>
                <p className="opacity-90">{t('cta.stat2Desc')}</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{t('cta.stat3Title')}</p>
                <p className="opacity-90">{t('cta.stat3Desc')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
